import React, { useContext, useEffect, useState } from 'react';
import { Button, LinearProgress, Typography } from '@mui/material';
import useGlynt from './useGlynt';
import { AppContext } from '../../App';
import { MarkDispatchContext, MarkStateContext } from '../../context/MarkContext';
import { differenceInMinutes, parseISO } from 'date-fns';
import { getIdFromUrl } from '../../utils/getIdFromUrl';
import { makePage } from '../../utils/makePage';
import { getTokenizedByTkID, addTokenized } from '../../utils/indexedDb';
import { useMutation, useQuery } from '@tanstack/react-query';

// TODO: need to add some comments on what does this hook is doing, or refactor to smaller functions
// it may be an anti-pattern to return jsx in a hook
// https://www.reddit.com/r/reactjs/comments/9yq1l8/how_do_you_feel_about_a_hook_returning_components/?sort=new
export default function useTokenized(
  tokenizedUrl: string,
  docId: string,
  docLabel: string,
  isReview: boolean,
  trainingSetId?: string
) {
  const glynt = useGlynt();
  const { setToast } = useContext(AppContext);
  const { tokenizerVersion } = useContext(MarkStateContext);
  const dispatch = useContext<React.Dispatch<any>>(MarkDispatchContext);
  const [onDB, updateOnDB] = useState(true);
  const [interval, setInterval] = useState<number | false | undefined>(5000);
  const [called, setCalled] = useState<boolean>(false);
  const [disablePolling, setDisablePolling] = useState(false);
  const [tokenPages, setTokenPages] = useState<any[]>([]);

  let tokenizedId = getIdFromUrl(tokenizedUrl);

  const { mutate: createTokenized, data: result } = useMutation({
    mutationFn: async (data: any) => {
      return await (await glynt).tokenizeds.create(data);
    }
  });

  const tokenizedCreate = async () => {
    try {
      await createTokenized({
        document: docId,
        tokenizer_version: 'default',
      });
      setTimeout(() => {
        setCalled(true);
      }, 100);
    } catch (error) {
      setTimeout(() => {
        setToast({
          open: true,
          variant: 'error',
          message: 'Tokenization failed',
        });
      }, 100);
    }
  };

  // Get tokenized from Indexeddb and generate pages if exists
  useEffect(() => {
    async function getFromDB() {
      try {
        const data = await getTokenizedByTkID(tokenizedId);
        const tokenized = data?.tokenized;
        updateOnDB(false);
        setTokenPages([]);
        if (!(tokenized && (tokenized as any).expiredDate))
          throw new Error('No expiration date found');
        const ageInMinute = differenceInMinutes(
          new Date(),
          parseISO((tokenized as any).expiredDate)
        );
        if (!(ageInMinute < 0)) throw new Error('Data in indexeddb already expired');
        const pages = Object.keys(tokenized)
          .filter((page) => !['Meta', 'updatedDate', 'expiredDate'].includes(page))
          .map((page) => makePage(docLabel, docId, tokenized, page));
        updateOnDB(true);
        setTokenPages(pages);
      } catch (error) {
        setTokenPages([]);
        updateOnDB(false);
      }
    }

    if (tokenizedId.length !== 0 || isReview) {
      getFromDB();
    } else if (tokenizedId.length === 0 && !called) {
      // when no tokenized found
      tokenizedCreate();
    }

    // stop polling after 6 tries (30s)
    setTimeout(() => {
      setDisablePolling(true);
    }, 5000 * 6);
  }, [tokenizedId]); // eslint-disable-line

  // get newly created tokenized id
  if (result && getIdFromUrl(result.document) === docId) {
    tokenizedId = result.id;
  }

  // Restart polling if docId is changed
  useEffect(() => {
    setInterval(2500);
  }, [docId]);

  // To avoid polling even when query was skipped
  useEffect(() => {
    if (tokenPages.length > 0) {
      setInterval(false);
    }
  }, [tokenPages, setInterval]);

  let queryEnabled = !(tokenPages.length > 0) && !(tokenizedId.length === 0) && !onDB;
  const {
    isPending: loading,
    data,
    error,
  } = useQuery({
    queryKey: ['tokenized', tokenizedId],

    queryFn: async () => {
      const glynt_client = await glynt;
      if (isReview) {
        return await glynt_client.tokenizeds.retrieve(tokenizedId);
      } else if (trainingSetId) {
        await glynt_client.trainingSet.refreshTokenizeds(trainingSetId);
        return await glynt_client.tokenizeds.retrieveRefreshed(tokenizedId);
      } else {
        return await glynt_client.tokenizeds.retrieveRefreshed(tokenizedId);
      }
    },

    refetchInterval: interval,
    enabled: queryEnabled
  });

  if (tokenPages.length > 0) {
    return tokenPages;
  }

  if (loading) {
    return <LinearProgress />;
  }

  if (error) {
    return <div>Sorry, failed to load tokenized</div>;
  }

  if (data && data.tokenized) {
    if (tokenizerVersion.length === 0) {
      setTimeout(() => {
        dispatch({
          type: 'TOKENIZER_VERSION',
          payload: data.tokenizer_version,
        });
      }, 100);
    }

    // polling for tokenized
    if (data.status === 'Pending' || data.status === 'In Progress') {
      return (
        <Typography style={{ color: 'orange' }}>
          THE DOCUMENT IS NOT READY.
          <br />
          HANG ON, THIS PAGE WILL RELOAD ONCE ALL NEEDED DATA IS READY. <br />
          TOKENIZATION STATUS: {data.status.toUpperCase()}
        </Typography>
      );
    } else if (data.status === 'Failed') {
      if (!called) {
        setCalled(true);
        tokenizedCreate();
      }
      return (
        <Typography style={{ color: 'orange' }}>
          TOKENIZATION FAILED. <Button onClick={tokenizedCreate}>Try Again</Button>
          <br />
          IF THE PROBLEM PERSIST, PLEASE CONTACT GLYNT SUPPORT.
        </Typography>
      );
    } else if (data.status === 'Succeeded' || data.status === 'Success') {
      setInterval(false);

      addTokenized({
        docID: getIdFromUrl(data.document),
        version: data.tokenizer_version,
        tokenized: data.tokenized,
        tkID: data.id,
      }).then(
        () => {
          updateOnDB(true);
          // stored in IndexedDB
        },
        () => {}
      );

      const pages = Object.keys(data.tokenized)
        .filter((page) => !['Meta', 'updatedDate', 'expiredDate'].includes(page))
        .map((page) => makePage(docLabel, docId, data.tokenized, page));
      updateOnDB(true);
      setTokenPages(pages);
    }
  }

  return tokenPages;
}
